import { Fragment, useEffect, useMemo, useRef, useState, type ReactNode } from 'react';
import { useNoticeToast } from '../lib/toast';
import { useFitHeight } from '../lib/fitHeight';
import { TopScrollbar } from '../components/TopScrollbar';
import { api, fetchDriverInvoiceFileUrl, fetchInvoiceRequestFileUrl, uploadInvoiceRequestFile, type InvReqFile, type AcctCorp, type InvoiceCandidate, type InvoiceRow } from '../lib/api';
import { ErrorBox, ResizeHandle, dash, money } from '../components/DataTable';
import Modal from '../components/Modal';
import { downloadSmartbill, type SmartbillKind } from '../lib/smartbill';
import DriverInvoiceImport, { DriverSyncLine } from './DriverInvoiceImport';
import { DriverNoticePanel } from './DriverNotice';
import { DriverInvoicePdfPanel } from './DriverInvoicePdf';
import { FixedPanel, FixedRuleForm, ProxyRequestForm } from './DriverProxy';
import { canScreen, useMe } from '../lib/me';
import { useSticky, useAcctCorp } from '../lib/sticky';
import { SortTh, useSort, useSortedRows } from '../lib/sort';
import { colWidthStyle, useTablePrefs } from '../lib/tableCols';

/**
 * 회계 2단계 E — 세금계산서 발행대장(#11). 기획 정본 `plan/accounting.md` 5-E.
 * 🔴 「청구대상 불러오기」는 10-16 회의 #36 으로 화면에서 뺐다 — 회사 계산서는 영업 「청구 요청」(발행 요청)으로만 들어온다.
 * ⚠️ cutover 전 청구대상은 이관본 기준이다(버스인 원본이 아니다) — 화면에 안내한다.
 */

const kst = () => new Date(Date.now() + 9 * 3600_000).toISOString().slice(0, 10);
const errMsg = (e: unknown) => (e instanceof Error ? e.message : String(e));
/** 한 쪽에 그리는 줄 수. 🔴 1,900여 장을 한 번에 그리면(DOM 4만여 개) 패널 열기·체크·검색 입력마다 표 전체를 다시 재서
 *  느린 PC 에서 「응답없음」이 된다(2026-09-24 way 신고, 실측 2초 멈춤). 합계는 서버가 전체로 낸다. */
const PAGE = 200;
/** 화면 설명 — 예전엔 표 위 문단(한 줄 자르기)이었다 → ⓘ 툴팁(10-21 way 「위쪽 최대한 줄이기」). */
const HINT = [
  '발행번호 = 연도-법인코드-순번(파주1·용인2·투어3·서울4·이천5). 발행자를 적으면 청구완료, 입금일자를 적으면 입금완료가 붙습니다(발행 전인데 입금된 건은 「발행 전 입금완료」). 줄을 누르면 고칩니다.',
  '입금일자는 「입금 내역」에서 은행 입금과 연결하면 자동으로 찍힙니다(「연결」 표시). 발행자·입금일자는 버스인웹 미수(청구완료·입금완료)에도 그대로 반영됩니다.',
  '기사개인오더: 「기사개인오더」 버튼 — 회사 계산서와 똑같이 여기서 고치고 발행자·입금일자를 적습니다(웹이 정본). 새 신청은 기사 앱에서 바로 들어옵니다 · 스마트빌 비고1 = 기사매출 계좌.',
  '스마트빌: 왼쪽 칸을 골라 「스마트빌 세금계산서」 또는 「스마트빌 거래명세서」 → 받은 파일을 스마트빌의 같은 이름 대량 업로드에 올립니다(서식이 달라 섞어 올리면 안 됩니다). 비고1 = 법인 입금 계좌(자동) · 품목 비면 「차량운행료」 · 비고·참조자 2 는 계산서 창에서.',
].join('\n\n');

function Status({ r }: { r: InvoiceRow }) {
  if (r.amend_of) return <span className="rounded bg-red-50 px-1.5 text-xs text-red-700">{Number(r.total) < 0 ? '수정(−)' : '같은 번호'}</span>;
  // 발행(발행 전/청구완료)과 입금은 따로 보인다(#47) — 기사 계산서는 입금이 먼저라 「입금완료」 하나만 보이면 아직 발행 안 한 건이 가려진다.
  const issued = r.issuer
    ? <span className="rounded bg-blue-50 px-1.5 text-xs text-blue-700">청구완료</span>
    : <span className="rounded bg-canvas px-1.5 text-xs text-ink-soft">발행 전</span>;
  if (!r.paid_date) return issued;
  return <>{issued} <span className="rounded bg-emerald-50 px-1.5 text-xs text-emerald-700">입금완료</span></>;
}

/** 머리글 — [이름, 정렬 키]. 상태는 여러 칸으로 정해지는 표시라 정렬 안 함. */
const INV_HEAD: [string, string | undefined][] = [
  ['상태', undefined], ['정산월', 'drive_mon'], ['접수번호', 'recv_id'], ['청구법인', 'corp_name'], ['청구방법', 'recv_method'], ['발행방식', 'issue_way'], ['발행번호', 'invoice_no'], ['버스인접수담당자', 'requester'], ['거래처명', 'cust_name'],
  ['사업자번호', 'biz_number'], ['요청일자', 'request_date'], ['작성일자', 'written_date'], ['발행일자', 'issue_date'], ['공급가액', 'supply'], ['세액', 'vat'], ['합계', 'total'],
  // 발행자·입금일자는 합계 바로 뒤(#162 — 맨 끝에 두면 100% 화면에서 잘려 안 보였다). 글이 긴 이메일·품목·비고를 뒤로.
  ['발행자', 'issuer'], ['입금일자', 'paid_date'],
  // #205 — 기사 계산서 배분(누구에게 얼마) · 예전엔 비고 툴팁에만 있었다.
  ['배분', undefined], ['이메일', 'email'], ['품목', 'item'], ['비고', 'memo'],
];

/** #165 — 기사가 취소 신청한 줄(기사접수 시트의 마이너스 줄 · 비고 「취소신청」) = 파랑(옛 세금계산서 시트 색). */
const DRIVER_CANCEL_BG = 'bg-blue-100';

/** 상태 칸 바로 뒤에 끼우는 칸(#161 후속 — 발행 요청 탭이 요청 상태·최근 소식을 붙인다). 🔴 부모에서 useMemo 로 — 표 몸통 memo 의 의존값이다. */
export interface InvoiceExtraCols { head: string[]; cells: (r: InvoiceRow) => ReactNode }

/** `requestOnly` = 발행 요청으로 이어진 계산서만(#161 후속 — 「발행 요청」 탭 아래 표) · `requestScope` = 그 탭 위 칩(진행 중·끝난 것·전체 — #161 후속 3). */
export function InvoiceTab({ corps, requestOnly = false, requestScope = 'active', extra, reloadKey = 0, corpCtl }: { corps: AcctCorp[]; requestOnly?: boolean; requestScope?: 'active' | 'done' | 'all'; extra?: InvoiceExtraCols;
  /** 바깥(발행 요청 탭)이 요청 창을 닫을 때마다 올린다 — 번호를 매기면 아래 표에 바로 보이게(#308). */
  reloadKey?: number;
  /** #338 — 발행 요청 탭이 위 요청 목록과 같은 법인 고르기를 넘긴다(두 칸이 따로 놀지 않게 · 이 표의 고르기 칸은 숨김). */
  corpCtl?: readonly [number | null, (v: number | null) => void] }) {
  const tableBox = useRef<HTMLDivElement>(null);
  const tableH = useFitHeight(tableBox);
  const [year, setYear] = useSticky('acct.inv.year', Number(kst().slice(0, 4)));
  // 정산월(#40) — 고르면 발행연도와 무관하게 그 달 계산서만(서버가 연도 조건을 뺀다).
  const [driveMon, setDriveMon] = useSticky<string | null>('acct.inv.driveMon', null);
  const [months, setMonths] = useState<string[]>([]);
  // 발행 요청 탭은 법인 고르기를 따로(기본 전체) — 회계 공용 법인(예: 파주)을 따라가면 다른 법인 요청에 번호를 매겨도 아래 표에 안 보였다(#308).
  const [sharedCorp, setSharedCorp] = useAcctCorp();
  const [reqCorp, setReqCorp] = useSticky<number | null>('acct.invreq.corp', null);
  const [corpId, setCorpId] = corpCtl ?? (requestOnly ? [reqCorp, setReqCorp] as const : [sharedCorp, setSharedCorp] as const);
  const [status, setStatus] = useSticky<'all' | 'unissued' | 'billed' | 'unpaid' | 'paid'>('acct.inv.status', 'all');
  // 기본 = 회사오더 — 기사개인오더(기사접수 시트, 5-H)가 섞이면 보던 합계·미입금이 바뀐다.
  const [kind, setKind] = useSticky<'company' | 'driver' | 'all'>('acct.inv.kind', 'company');
  const [driverImport, setDriverImport] = useState(false);
  const [term, setTerm] = useSticky('acct.inv.term', '');
  const [q, setQ] = useSticky('acct.inv.q', '');
  const [nonce, setNonce] = useState(0);
  const [data, setData] = useState<Awaited<ReturnType<typeof api.listInvoices>> | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);
  const [editing, setEditing] = useState<InvoiceRow | 'new' | null>(null);
  const [picked, setPicked] = useState<Set<string>>(new Set());
  /** 지금 만드는 파일(#124 — 누른 버튼만 「만드는 중…」. 하나로 두면 두 버튼이 같이 눌린 것처럼 보였다). */
  const [sbBusy, setSbBusy] = useState<SmartbillKind | 'bulk' | null>(null);
  const [sbNotice, setSbNotice] = useState<string | null>(null);
  const [pdfOpen, setPdfOpen] = useState(false);
  const [bulk, setBulk] = useState<{ issuer: string; date: string } | null>(null); // #46 발행자 일괄 입력
  const [page, setPage] = useState(0);
  const [misuPanel, setMisuPanel] = useState(false);
  // #206 기사 매출 대리 처리 — 기사 대신 신청 · 매월 고정발행 창 · 「매월 고정으로」(그 계산서 번호)
  const [proxyOpen, setProxyOpen] = useState(false);
  const [fixedOpen, setFixedOpen] = useState(false);
  const [fixedFrom, setFixedFrom] = useState<number | null>(null);
  const { me } = useMe();
  const canWrite = canScreen(me, 'accounting', 'write');

  useEffect(() => {
    let cancelled = false;
    setLoading(true); setError(null);
    // 발행 요청 탭(requestOnly) = 일거리 목록 — 연도·정산월·상태는 서버가 안 건다(같은 sticky 값을 세금계산서 탭과 공유하므로 여기선 보내지 않는다).
    api.listInvoices(requestOnly ? { year, driveMon: null, corpId, status: 'all', kind, q: q || undefined, requestOnly, requestScope }
      : { year, driveMon, corpId, status, kind, q: q || undefined, requestOnly })
      .then((r) => { if (!cancelled) { setData(r); if (r.months) setMonths(r.months); setPicked(new Set()); setPage(0); } })
      .catch((e) => { if (!cancelled) setError(errMsg(e)); })
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, [year, driveMon, corpId, status, kind, q, nonce, requestOnly, requestScope, reloadKey]);
  const reload = () => setNonce((n) => n + 1);
  const grand = data?.totals.find((t) => t.corp_name === null);
  const total = data?.rows.length ?? 0;
  // 머리글 정렬(#37) — 목록을 전부 받아 두니 화면이 정렬한다(쪽 나누기 전에).
  const [sort, toggleSort] = useSort('acct.inv');
  // #327 — 머리글 오른쪽 끝을 끌어 칸 너비 조절(엑셀처럼 · 계정에 기억) · 발행 요청 탭 아래 표와 세금계산서 탭은 칸 구성이 달라 따로 기억.
  const { prefs, setWidth, reset: resetCols, customized } = useTablePrefs(requestOnly ? 'acct.invreq' : 'acct.inv');
  const anyWidth = Object.keys(prefs.width).length > 0;
  const colW = (k: string) => (anyWidth ? colWidthStyle(prefs, k) : undefined);
  const onSort = (k: string) => { toggleSort(k); setPage(0); };
  const sorted = useSortedRows(data?.rows, sort);
  // 머리 칸 「전체 고르기」는 **이 쪽** 만 — 안 보이는 줄까지 골라 스마트빌 표시가 찍히면 안 된다.
  const selectable = useMemo(() => sorted.slice(page * PAGE, (page + 1) * PAGE).filter((r) => !r.amend_of), [sorted, page]);
  const pages = Math.max(1, Math.ceil(total / PAGE));
  const toggle = (id: string, on: boolean) => setPicked((p) => { const n = new Set(p); if (on) n.add(id); else n.delete(id); return n; });

  // 표 몸통은 보이는 쪽·고른 칸이 바뀔 때만 다시 만든다(검색어 입력·패널 열기는 표를 건드리지 않는다).
  const body = useMemo(() => sorted.slice(page * PAGE, (page + 1) * PAGE).map((r) => (
    <tr key={r.invoice_id} onDoubleClick={() => setEditing(r)}
        title={r.source === 'driver' && r.amend_of ? '기사 취소 신청 줄' : undefined}
        className={`cursor-pointer border-b hover:bg-brand-50/40 ${Number(r.total) < 0 ? 'text-red-600' : ''} ${r.source === 'driver' && r.amend_of ? DRIVER_CANCEL_BG : ''}`}>
      <td className="px-2" onClick={(e) => e.stopPropagation()}>
        {!r.amend_of && <input type="checkbox" checked={picked.has(r.invoice_id)} onChange={(e) => toggle(r.invoice_id, e.target.checked)} />}
      </td>
      <td className="whitespace-nowrap px-2 py-1.5">{(!requestOnly || requestScope !== 'active') && <Status r={r} />}
        {r.smartbill_at && <span className="ml-1 rounded bg-violet-50 px-1 text-[10px] text-violet-700" title={`스마트빌 파일(세금계산서·거래명세서) 만듦 ${r.smartbill_at}`}>스마트빌</span>}
        {r.source === 'sheet' && <span className="ml-1 text-[10px] text-ink-faint">시트</span>}
        {r.source === 'driver' && <span className="ml-1 rounded bg-amber-50 px-1 text-[10px] text-amber-800" title={r.from_app ? '기사개인오더 — 기사 앱에서 신청' : '기사개인오더 — 기사접수 시트에서 옮겨 온 계산서'}>{r.from_app ? '기사·앱' : '기사'}</span>}
        {r.notified_at && <span className="ml-1 rounded bg-sky-50 px-1 text-[10px] text-sky-700" title={`발행 알림톡 보냄 ${r.notified_at}`}>알림</span>}
        {r.by_acct && <span className="ml-1 rounded bg-orange-50 px-1 text-[10px] text-orange-700" title={`회계팀이 기사 대신 신청(${r.by ?? ''})`}>대리</span>}
        {r.by_driver && <span className="ml-1 rounded bg-amber-100 px-1 text-[10px] text-amber-800" title={`${r.by_driver} 기사가 대신 신청`}>대신</span>}
        {r.fixed_id && <span className="ml-1 rounded bg-teal-50 px-1 text-[10px] text-teal-700" title={`매월 고정발행 #${r.fixed_id} · ${r.fixed_month ?? ''}`}>고정</span>}
        {r.attach_name && <button className="ml-1 text-xs" title={`첨부 보기: ${r.attach_name}`} onClick={(e) => { e.stopPropagation(); void openAttach(Number(r.invoice_id)); }}>📎</button>}
        {/* #127 — 시트로 신청한 계산서는 사진이 구글드라이브 링크다(기사 앱 신청분은 위 📎). */}
        {!r.attach_name && r.attach_url && <a className="ml-1 text-xs" href={r.attach_url} target="_blank" rel="noreferrer" title="첨부 보기: 기사가 올린 사업자등록증" onClick={(e) => e.stopPropagation()}>📎</a>}</td>
      {extra?.cells(r)}
      <td className="px-2">{dash(r.drive_mon)}</td><td className="px-2">{dash(r.recv_id)}</td>
      <td className="whitespace-nowrap px-2">{r.corp_name}</td>
      {/* #232·#234 — 접수의 청구방법 · 영업이 고른 발행방식(정발행이 아니면 눈에 띄게) */}
      <td className="whitespace-nowrap px-2">{dash(r.recv_method)}</td>
      <td className={`whitespace-nowrap px-2 ${r.issue_way && r.issue_way !== '정발행' ? 'font-semibold text-violet-700' : ''}`}>{dash(r.issue_way)}</td>
      <td className="whitespace-nowrap px-2 font-mono">{r.invoice_no}</td><td className="px-2">{dash(r.requester)}</td>
      <td className="max-w-[11rem] truncate px-2" title={r.cust_name ?? ''}>{r.cust_name}</td><td className="whitespace-nowrap px-2">{dash(r.biz_number)}</td>
      <td className="whitespace-nowrap px-2" title={r.amend_of ? '영업이 취소·수정을 요청한 날' : '영업이 발행을 요청한 날'}>{dash(r.request_date)}</td>
      <td className="whitespace-nowrap px-2">
        {dash(r.written_date)}
        {/* #536 작성일자가 오늘(KST) 뒤면 「예정」 — 미리 올린 계산서를 한눈에 */}
        {r.written_date && r.written_date.slice(0, 10) > kst()
          ? <span className="ml-1 rounded-full bg-sky-100 px-1.5 py-0.5 text-[11px] font-medium text-sky-700" title="작성일자가 오늘 이후인 계산서">예정</span>
          : null}
      </td><td className="whitespace-nowrap px-2">{dash(r.issue_date)}</td>
      <td className="px-2 text-right">{money(r.supply)}</td><td className="px-2 text-right">{money(r.vat)}</td>
      <td className="px-2 text-right font-medium">{money(r.total)}</td>
      <td className="whitespace-nowrap px-2">{dash(r.issuer)}</td><td className="whitespace-nowrap px-2">{dash(r.paid_date)}{r.paid_by_match && <span className="ml-1 rounded bg-emerald-50 px-1 text-[10px] text-emerald-700" title="입금 내역에서 은행 입금과 연결해 찍힌 입금일자">연결</span>}</td>
      <td className="max-w-[16rem] truncate px-2 text-amber-900" title={r.dists ? `배분: ${r.dists}` : ''}>{dash(r.dists)}</td>
      <td className="max-w-[10rem] truncate px-2" title={r.email ?? ''}>{dash(r.email)}</td>
      <td className="max-w-[8rem] truncate px-2" title={r.item ?? ''}>{dash(r.item)}</td><td className="max-w-[10rem] truncate px-2" title={[r.memo, r.phone && `연락처 ${r.phone}`].filter(Boolean).join('\n')}>{dash(r.memo)}</td>
    </tr>
  )), [sorted, page, picked, extra, requestOnly, requestScope]);

  async function openAttach(id: number) {
    try { const url = await fetchDriverInvoiceFileUrl(id); window.open(url, '_blank'); setTimeout(() => URL.revokeObjectURL(url), 60_000); }
    catch (e) { setError(errMsg(e)); }
  }

  /** plan/driver-orders.md ③ 전환일 — 기사 앱 신청 열기·닫기. 두 번 눌러야 바뀐다(실수 방지). */
  const [openArm, setOpenArm] = useState(false);
  async function toggleDriverOpen() {
    if (!openArm) { setOpenArm(true); return; }
    setOpenArm(false);
    try { await api.setDriverOrderOpen({ open: !data?.driverOpen }); reload(); } catch (e) { setError(errMsg(e)); }
  }

  /** #14·#120 — 고른 계산서를 스마트빌 대량업로드 파일로(법인마다 한 파일). 세금계산서·거래명세서 서식이 다르다. */
  async function smartbill(kind: SmartbillKind) {
    setSbBusy(kind); setError(null); setSbNotice(null);
    try {
      const r = await api.exportSmartbill({ ids: [...picked].map(Number), kind });
      const ymd = kst().replace(/-/g, '');
      for (const f of r.files) await downloadSmartbill(kind, f.corpName, f.rows, ymd);
      const made = r.files.map((f) => `${f.corpName} ${f.rows.length}장`).join(' · ');
      const skip = r.skipped.length ? ` — 빠진 ${r.skipped.length}장: ${r.skipped.slice(0, 5).map((x) => `${x.invoiceNo}(${x.reason})`).join(', ')}${r.skipped.length > 5 ? ' …' : ''}` : '';
      const label = kind === 'statement' ? '스마트빌 거래명세서' : '스마트빌 세금계산서';
      setSbNotice(made ? `${label} 파일을 만들었습니다: ${made}${skip}` : `만들 수 있는 계산서가 없습니다${skip}`);
      reload();
    } catch (e) { setError(errMsg(e)); } finally { setSbBusy(null); }
  }

  /** #161 — 고른 계산서를 한 번에 청구완료로: 발행자 = 누른 사람(서버가 채운다) · 발행일자 = 오늘. 이미 발행자가 있는 줄은 그대로. */
  async function markBilled() {
    setSbBusy('bulk'); setError(null); setSbNotice(null);
    try {
      const r = await api.setInvoiceIssuerBulk({ ids: [...picked].map(Number), self: true, issueDate: kst() });
      setSbNotice(`청구완료로 바꿨습니다: ${r.updated}장${r.skipped ? ` · 이미 발행자가 있거나 수정 발행 줄이라 그대로 둔 ${r.skipped}장` : ''}`);
      setPicked(new Set()); reload();
    } catch (e) { setError(errMsg(e)); } finally { setSbBusy(null); }
  }

  /** #46 — 고른 계산서에 발행자·발행일자를 한 번에(이미 발행자가 있는 줄은 그대로 둔다). */
  async function saveBulk() {
    if (!bulk) return;
    setSbBusy('bulk'); setError(null); setSbNotice(null);
    try {
      const r = await api.setInvoiceIssuerBulk({ ids: [...picked].map(Number), issuer: bulk.issuer.trim(), issueDate: bulk.date || null });
      setSbNotice(`발행자를 넣었습니다: ${r.updated}장${r.skipped ? ` · 이미 발행자가 있거나 수정 발행 줄이라 그대로 둔 ${r.skipped}장` : ''}`);
      setBulk(null); setPicked(new Set()); reload();
    } catch (e) { setError(errMsg(e)); } finally { setSbBusy(null); }
  }

  return (
    <div className="space-y-1.5">
      <div className="btn-row-sm flex flex-wrap items-center gap-x-2 gap-y-1">
        {/* 구분 버튼(#161)은 조건 줄 맨 앞 · 설명은 ⓘ 툴팁(10-21 way 「위쪽 최대한 줄이고 아래 표가 더 보이게」). 고른 구분은 화면 메모리만. */}
        <div className="flex items-center gap-1" role="tablist" aria-label="계산서 구분">
          {([['company', '회사오더'], ['driver', '기사개인오더'], ['all', '회사+기사']] as const).map(([k, label]) => (
            <button key={k} type="button" role="tab" aria-selected={kind === k} onClick={() => { setKind(k); setPicked(new Set()); }}
                    title={k === 'company' ? '버스인 청구·수기 발행' : k === 'driver' ? '기사접수 앱으로 신청한 계산서' : '둘 다'}
                    className={`rounded-lg border px-2 py-0.5 text-[13px] font-medium ${kind === k ? 'border-brand-500 bg-brand-50 text-brand-700' : 'border-line text-ink-soft hover:bg-canvas'}`}>
              {label}
            </button>
          ))}
          <span className="cursor-help px-1 text-sm text-ink-faint" title={HINT}>ⓘ</span>
        </div>
        {!requestOnly && (<>
        <select className="field w-24" value={year} onChange={(e) => setYear(Number(e.target.value))}
                disabled={driveMon !== null} title={driveMon ? '정산월을 고르면 발행연도와 상관없이 그 달 계산서가 모두 보입니다' : undefined}>
          {[0, 1, 2].map((d) => { const y = Number(kst().slice(0, 4)) - d; return <option key={y} value={y}>{y}년</option>; })}
        </select>
        <select className="field w-36" value={driveMon ?? ''} onChange={(e) => setDriveMon(e.target.value || null)}>
          <option value="">전체 정산월</option>
          {months.map((m) => <option key={m} value={m}>{m} 정산</option>)}
        </select>
        </>)}
        {/* #362 최여진 — 발행 요청 탭은 위 목록과 같은 값(corpCtl)을 위·아래 두 곳에서 고른다(#338 에서 아래를 숨겼던 것을 되살림). */}
        <select className="field w-40" value={corpId ?? ''} onChange={(e) => setCorpId(e.target.value ? Number(e.target.value) : null)}>
          <option value="">전체 법인</option>
          {corps.filter((c) => c.invoice_code).map((c) => <option key={c.corp_id} value={c.corp_id}>{c.short_name}({c.invoice_code})</option>)}
        </select>
        {!requestOnly && <select className="field w-32" value={status} onChange={(e) => setStatus(e.target.value as typeof status)}>
          <option value="all">전체</option><option value="unissued">발행 전</option><option value="billed">청구완료</option>
          <option value="unpaid">미입금</option><option value="paid">입금완료</option>
        </select>}
        <form onSubmit={(e) => { e.preventDefault(); setQ(term.trim()); }} className="flex items-center gap-2">
          <input className="field w-56" placeholder="거래처·번호·사업자·접수·담당" value={term} onChange={(e) => setTerm(e.target.value)} />
          <button className="btn-search" type="submit" aria-label="검색" title="검색" />
        </form>
        <div className="ml-auto flex gap-2">
          {/* #408 — 스마트빌에서 받은 계산서 PDF → 쪽마다 관리번호로 짝지어 기사 알림톡 */}
          {kind === 'driver' && canWrite && (
            <button className="btn" onClick={() => setPdfOpen(true)} title="스마트빌에서 받은 계산서 PDF(여러 장)를 올리면 쪽마다 관리번호를 읽어 발행번호와 짝짓고 신청 기사에게 알림톡을 보냅니다">세금계산서 파일 업로드</button>
          )}
          <button className="btn" onClick={() => void smartbill('tax')} disabled={sbBusy !== null ||picked.size === 0}
            title="고른 계산서를 스마트빌 「세금계산서 대량 업로드」 서식으로 내려받습니다(법인마다 한 파일)">
            {sbBusy === 'tax' ? '만드는 중…' : `스마트빌 세금계산서${picked.size ? ` (${picked.size})` : ''}`}
          </button>
          <button className="btn" onClick={() => void smartbill('statement')} disabled={sbBusy !== null ||picked.size === 0}
            title="고른 계산서를 스마트빌 「거래명세서 대량 업로드」 서식으로 내려받습니다(법인마다 한 파일)">
            {sbBusy === 'statement' ? '만드는 중…' : `스마트빌 거래명세서${picked.size ? ` (${picked.size})` : ''}`}
          </button>
          <button className="btn" onClick={() => void markBilled()} disabled={sbBusy !== null || picked.size === 0}
            title="고른 계산서를 한 번에 청구완료로 — 발행자에 내 이름, 발행일자에 오늘이 들어갑니다(이미 발행자가 있는 줄은 그대로)">
            {`청구완료${picked.size ? ` (${picked.size})` : ''}`}
          </button>
          <button className="btn" onClick={() => setBulk(bulk ? null : { issuer: '', date: kst() })} disabled={sbBusy !== null ||picked.size === 0}
            title="고른 계산서에 발행자·발행일자를 한 번에 넣어 청구완료로 바꿉니다(이미 발행자가 있는 줄은 그대로)">
            {`발행자 일괄 입력${picked.size ? ` (${picked.size})` : ''}`}
          </button>
          {kind === 'driver' ? (
            <>
              {/* 10-15 — 회계 계정이 이 버튼으로 닫아 기사앱 계산서가 옛 앱으로 넘어갔다 → 관리자만 버튼, 그 밖엔 상태 글자만. */}
              {me?.role === 'admin' ? (
                <button className={`btn ${openArm ? 'border-red-400 text-red-700' : ''}`} onClick={() => void toggleDriverOpen()} onBlur={() => setOpenArm(false)}
                        title="기사 앱(businweb /driver)에서 세금계산서 신청을 받을지(관리자만). 닫으면 기사앱 세금계산서 칸이 옛 세금계산서 앱으로 갑니다.">
                  {openArm ? (data?.driverOpen ? '정말 닫을까요? 한 번 더' : '정말 열까요? 한 번 더') : `기사 앱 신청: ${data?.driverOpen ? '열림' : '닫힘'}`}
                </button>
              ) : (
                <span className="self-center text-xs text-ink-soft">기사 앱 신청: {data?.driverOpen ? '열림' : '닫힘'}</span>
              )}
              {!data?.driverOpen && <button className="btn-primary" onClick={() => setDriverImport(true)} title="기사들이 기사접수 앱(시트)으로 신청하는 동안만 쓰는 버튼">기사 새 신청 가져오기</button>}
              <button className="btn" onClick={() => setFixedOpen(true)} title="#206 — 달마다 같은 계산서를 받는 기사(매월 고정발행) · 이번 달 만들기">고정발행</button>
              {canWrite && <button className="btn-primary" onClick={() => setProxyOpen(true)} title="#206 — 회계팀이 기사 대신 세금계산서를 신청합니다(기사 앱과 같은 번호·신청자)">기사 대신 신청</button>}
            </>
          ) : (<>
            <button className="btn-ghost" onClick={() => setMisuPanel(true)} title="발행자·입금일자를 버스인웹 미수(청구·입금 상태)에 반영">미수 반영</button>
            <button className="btn-ghost" onClick={() => setEditing('new')}>수기 발행</button>
          </>)}
        </div>
      </div>
      {bulk && picked.size > 0 && (
        <form className="flex flex-wrap items-center gap-2 rounded-lg bg-blue-50 px-3 py-2 text-sm" onSubmit={(e) => { e.preventDefault(); void saveBulk(); }}>
          <span>고른 {picked.size}장에</span>
          <input className="field w-40" placeholder="발행자" value={bulk.issuer} autoFocus onChange={(e) => setBulk({ ...bulk, issuer: e.target.value })} />
          <input className="field w-40" type="date" value={bulk.date} onChange={(e) => setBulk({ ...bulk, date: e.target.value })} title="발행일자" />
          <button className="btn-primary" type="submit" disabled={sbBusy !== null ||!bulk.issuer.trim()}>넣기</button>
          <button className="btn-ghost" type="button" onClick={() => setBulk(null)}>취소</button>
        </form>
      )}
      {kind !== 'company' && data && <DriverSyncLine sync={data.driverSync} open={data.driverOpen} />}
      {sbNotice && <p className="rounded-lg bg-emerald-50 px-3 py-2 text-sm text-emerald-800">{sbNotice}</p>}
      {error && <ErrorBox message={error} />}
      {/* 법인별 합계 — 한 줄(넘치면 가로 밀기) · 작은 글자(10-21 way). */}
      <div className="scroll-quiet flex items-center gap-1.5 overflow-x-auto whitespace-nowrap text-xs">
        {data?.totals.filter((t) => t.corp_name).map((t) => (
          <span key={t.corp_name} className="rounded-md bg-canvas px-2 py-0.5">
            {t.corp_name} {t.n}장 · <b>{money(t.total)}</b>
            {Number(t.unpaid) > 0 && <span className="text-amber-700"> · 미입금 {money(t.unpaid)}</span>}
          </span>
        ))}
        {grand && <span className="rounded-md bg-brand-50 px-2 py-0.5 font-semibold">합계 {grand.n}장 · {money(grand.total)} · 미입금 {money(grand.unpaid)}</span>}
        {data && data.rows.length === 0 && !loading && <span className="text-ink-faint">이 조건의 계산서가 없습니다.</span>}
        {customized && <button type="button" className="btn-ghost text-xs" onClick={resetCols} title="끌어서 바꾼 칸 너비를 처음대로">칸 너비 처음대로</button>}
      </div>
      {/* 표를 화면 높이 안에 묶는다(#162 · issues 115) — 안 묶으면 가로 스크롤바가 200줄 아래에 생겨 오른쪽 칸을 볼 길이 없다.
          머리글은 sticky, 배경은 칸마다(row-group 배경은 sticky 에서 안 칠해진다). */}
      <TopScrollbar target={tableBox} />
      <div ref={tableBox} className="overflow-auto" style={{ maxHeight: tableH }}>
        <table className={`w-full min-w-[1500px] text-xs [&_thead_th]:sticky [&_thead_th]:top-0 [&_thead_th]:z-10 [&_thead_th]:bg-surface ${anyWidth ? 'table-fixed' : '[&_thead_th]:whitespace-nowrap'}`}>
          {anyWidth && (
            <colgroup>
              <col style={{ width: 32 }} />
              {INV_HEAD.map(([h], i) => (
                <Fragment key={h}>
                  <col style={colW(h)} />
                  {i === 0 && extra?.head.map((x) => <col key={x} style={colW(x)} />)}
                </Fragment>
              ))}
            </colgroup>
          )}
          <thead>
            <tr className="border-b text-left text-ink-soft">
              <th className="px-2 py-2"><input type="checkbox" aria-label="이 쪽 전체 고르기" title="이 쪽(최대 200장) 전체 고르기"
                checked={selectable.length > 0 && selectable.every((r) => picked.has(r.invoice_id))}
                onChange={(e) => setPicked((p) => { const n = new Set(p); for (const r of selectable) { if (e.target.checked) n.add(r.invoice_id); else n.delete(r.invoice_id); } return n; })} /></th>
              {INV_HEAD.map(([h, k], i) => (
                <Fragment key={h}>
                  <SortTh sortKey={k} sort={sort} onSort={onSort} fixed={anyWidth} after={<ResizeHandle onResize={(px) => setWidth(h, px)} />}
                          className={`px-2 py-2 ${['공급가액', '세액', '합계'].includes(h) ? 'text-right' : ''}`}>{requestOnly && requestScope === 'active' && i === 0 ? '구분' : h}</SortTh>
                  {i === 0 && extra?.head.map((x) => <SortTh key={x} sort={sort} onSort={onSort} fixed={anyWidth} after={<ResizeHandle onResize={(px) => setWidth(x, px)} />} className="px-2 py-2">{x}</SortTh>)}
                </Fragment>
              ))}
            </tr>
          </thead>
          <tbody className={`${loading ? 'opacity-50' : ''} ${anyWidth ? '[&_td]:overflow-hidden [&_td]:text-ellipsis' : ''}`}>
            {body}
          </tbody>
        </table>
      </div>
      {pages > 1 && (
        <div className="flex items-center gap-3 text-sm">
          <button className="btn-ghost" disabled={page === 0} onClick={() => setPage((p) => p - 1)}>이전</button>
          <span className="text-ink-soft">{page + 1} / {pages} 쪽 · {page * PAGE + 1}–{Math.min(total, (page + 1) * PAGE)} / {total}장</span>
          <button className="btn-ghost" disabled={page >= pages - 1} onClick={() => setPage((p) => p + 1)}>다음</button>
          {picked.size > 0 && <span className="text-ink-faint">고른 {picked.size}장은 쪽을 넘겨도 유지됩니다</span>}
        </div>
      )}
      {misuPanel && (
        <Modal width="max-w-2xl">
          <MisuSyncPanel onClose={() => setMisuPanel(false)} />
        </Modal>
      )}
      {driverImport && (
        <Modal width="max-w-4xl">
          <DriverInvoiceImport onClose={() => setDriverImport(false)} onDone={reload} />
        </Modal>
      )}
      {editing && (
        <Modal width="max-w-2xl">
          <InvoiceForm corps={corps} row={editing === 'new' ? null : editing} onClose={() => setEditing(null)} onSaved={() => { setEditing(null); reload(); }} onNotified={reload}
                       onMakeFixed={canWrite ? (id) => { setEditing(null); setFixedFrom(id); } : undefined} />
        </Modal>
      )}
      {proxyOpen && (
        <Modal width="max-w-3xl">
          <ProxyRequestForm mode="invoice" onClose={() => setProxyOpen(false)}
                            onSaved={(no, note) => { setProxyOpen(false); setSbNotice(`기사 대신 신청했습니다 — ${no}${note}`); reload(); }} />
        </Modal>
      )}
      {fixedOpen && (
        <Modal width="max-w-5xl">
          <FixedPanel canWrite={canWrite} onClose={() => setFixedOpen(false)} onChanged={reload} />
        </Modal>
      )}
      {pdfOpen && (
        <Modal width="max-w-4xl">
          <DriverInvoicePdfPanel onClose={() => setPdfOpen(false)} onDone={reload} />
        </Modal>
      )}
      {fixedFrom !== null && (
        <Modal width="max-w-3xl">
          <FixedRuleForm invoiceId={fixedFrom} onClose={() => setFixedFrom(null)} onSaved={(m) => { setFixedFrom(null); setSbNotice(m); }} />
        </Modal>
      )}
    </div>
  );
}

// ─── 미수 반영 ───────────────────────────────────────────────────────────────
/**
 * 계산서 → 버스인웹 미수 반영(plan/accounting.md 5-G 미수 해소). 켜 두면 발행자·입금일자·입금 연결이 바뀌는 순간 미수가 따라간다.
 * 여기서는 스위치(관리자)와 **밀린 반영**(켜기 전에 쌓인 것 — 버스인웹 전환 날 한 번)만 한다. 둘 다 두 번 눌러야 된다.
 */
function MisuSyncPanel({ onClose }: { onClose: () => void }) {
  const { me } = useMe();
  const [data, setData] = useState<Awaited<ReturnType<typeof api.previewMisuSync>> | null>(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [notice, setNotice] = useState<string | null>(null);
  useNoticeToast(notice, setNotice);
  const [arm, setArm] = useState<'commit' | 'switch' | null>(null);
  async function load() {
    setBusy(true); setError(null);
    try { setData(await api.previewMisuSync({})); } catch (e) { setError(errMsg(e)); } finally { setBusy(false); }
  }
  useEffect(() => { void load(); }, []);
  async function toggle() {
    if (!data) return;
    if (arm !== 'switch') { setArm('switch'); return; }
    setArm(null); setBusy(true); setError(null);
    try { await api.setMisuSync({ open: !data.open }); await load(); } catch (e) { setError(errMsg(e)); setBusy(false); }
  }
  async function commit() {
    if (!data) return;
    if (arm !== 'commit') { setArm('commit'); return; }
    setArm(null); setBusy(true); setError(null); setNotice(null);
    try {
      const r = await api.commitMisuSync({ bill: data.bill, paid: data.paid });
      setNotice(`반영했습니다 — 청구완료 ${r.bill}건 · 입금완료 ${r.paid}건`);
      await load();
    } catch (e) { setError(errMsg(e)); setBusy(false); }
  }
  const todo = data ? data.bill + data.paid : 0;
  return (
    <div className="card space-y-3 p-5 text-sm">
      <div className="flex items-center justify-between gap-2">
        <h2 className="font-bold">버스인웹 미수 반영</h2>
        {me?.role === 'admin' && data && (
          <button className={`btn text-xs ${arm === 'switch' ? 'border-red-400 text-red-700' : ''}`} disabled={busy}
            onClick={() => void toggle()} onBlur={() => setArm(null)}
            title="버스인웹 전환(전량 재적재) 뒤에 켭니다 — 그 전엔 버스인이 미수를 쓰고 있고, 웹에서 바꾼 것은 재적재가 지웁니다">
            {arm === 'switch' ? (data.open ? '정말 끌까요? 한 번 더' : '정말 켤까요? 한 번 더') : `미수 반영: ${data.open ? '켜짐' : '꺼짐'}`}
          </button>
        )}
      </div>
      <p className="text-ink-soft">
        켜 두면 계산서에 <b>발행자</b>를 적는 순간 그 접수·정산월의 미수가 <b>청구완료</b>(청구일자 = 발행일자)가 되고,
        그 접수의 계산서가 <b>전부 입금</b>되면 <b>입금완료</b>(입금일 = 마지막 입금일)가 됩니다. 입금 연결을 풀거나 발행자를 지우면 웹이 바꾼 그대로일 때만 되돌립니다.
        사람이 미수 화면에서 적은 값은 덮지 않습니다.
      </p>
      {!data?.open && <p className="rounded-lg bg-amber-50 px-3 py-2 text-amber-800">지금은 <b>꺼져 있습니다</b> — 버스인웹 전환 뒤 관리자가 켭니다. 아래 숫자는 켜면 반영될 양입니다.</p>}
      {error && <ErrorBox message={error} />}
      {data && (
        <div className="grid grid-cols-2 gap-2">
          <div className="rounded-lg bg-canvas px-3 py-2">계산서 있는 접수·정산월 <b>{data.units.toLocaleString()}</b></div>
          <div className="rounded-lg bg-canvas px-3 py-2">밀린 반영 — 청구완료 <b>{data.bill.toLocaleString()}</b> · 입금완료 <b>{data.paid.toLocaleString()}</b></div>
          <div className="rounded-lg bg-canvas px-3 py-2" title="버스인웹이 청구대상을 만들 때 생기는 줄이 없는 접수 — 만들지 않고 건너뜁니다">미수 줄 없음 {data.noMisu.toLocaleString()}</div>
          <div className="rounded-lg bg-canvas px-3 py-2" title="미수는 입금완료인데 계산서는 아직 입금 전 — 어느 쪽이 맞는지 확인이 필요합니다(자동으로 안 바꿉니다)">미수만 입금완료 {data.misuPaidInvoiceNot.toLocaleString()}</div>
        </div>
      )}
      {data && data.sample.length > 0 && (
        <div className="max-h-60 overflow-auto">
          <table className="w-full text-xs">
            <thead><tr className="border-b text-left text-ink-soft"><th className="px-2 py-1">접수</th><th className="px-2 py-1">정산월</th><th className="px-2 py-1">청구완료</th><th className="px-2 py-1">입금완료</th></tr></thead>
            <tbody>
              {data.sample.map((i) => (
                <tr key={`${i.recvId}-${i.driveMon}`} className="border-b">
                  <td className="px-2 py-1">{i.recvId}</td><td className="px-2 py-1">{i.driveMon}</td><td className="px-2 py-1">{dash(i.bill)}</td><td className="px-2 py-1">{dash(i.paid)}</td>
                </tr>
              ))}
            </tbody>
          </table>
          {todo > data.sample.length && <p className="pt-1 text-ink-faint">앞 {data.sample.length}건만 보입니다(전체 {todo.toLocaleString()}건).</p>}
        </div>
      )}
      <div className="flex justify-end gap-2">
        <button className="btn-ghost" onClick={onClose}>닫기</button>
        <button className={`btn-primary ${arm === 'commit' ? 'ring-2 ring-red-400' : ''}`} disabled={busy || !data?.open || todo === 0}
          onClick={() => void commit()} onBlur={() => setArm(null)}
          title={data?.open ? '켜기 전에 쌓인 것을 한 번에 — 전부 아니면 전무' : '미수 반영이 꺼져 있습니다'}>
          {arm === 'commit' ? `${todo.toLocaleString()}건 반영 — 한 번 더` : '밀린 반영'}
        </button>
      </div>
    </div>
  );
}

// ─── 수기 발행·고치기 ────────────────────────────────────────────────────────
function InvoiceForm({ corps, row, onClose, onSaved, onNotified, onMakeFixed }: {
  corps: AcctCorp[]; row: InvoiceRow | null; onClose: () => void; onSaved: () => void; onNotified?: () => void;
  /** #206 「매월 고정으로」 — 기사개인오더 계산서(수정 줄 아님)에서 매월 고정발행 규칙을 만든다. */
  onMakeFixed?: (invoiceId: number) => void;
}) {
  const [f, setF] = useState({
    corpId: row?.corp_id ?? corps.find((c) => c.invoice_code)?.corp_id ?? 0, series: row?.series ?? '',
    recvId: row?.recv_id ? String(row.recv_id) : '', driveMon: row?.drive_mon ?? '', recvKind: row?.recv_kind ?? '', requester: row?.requester ?? '',
    custName: row?.cust_name ?? '', bizNumber: row?.biz_number ?? '', email: row?.email ?? '',
    writtenDate: row?.written_date ?? kst(), issueDate: row?.issue_date ?? '',
    supply: row ? Number(row.supply).toLocaleString() : '', vat: row ? Number(row.vat).toLocaleString() : '',
    item: row?.item ?? '', memo: row?.memo ?? '', itemNote: row?.item_note ?? '', ccName: row?.cc_name ?? '', ccEmail: row?.cc_email ?? '', remark1: row?.remark1 ?? '', issuer: row?.issuer ?? '', paidDate: row?.paid_date ?? '',
  });
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [confirm, setConfirm] = useState<'del' | 'amend' | 'reject' | 'cancel' | null>(null);
  const [cancelWhy, setCancelWhy] = useState('');
  const [rejectWhy, setRejectWhy] = useState('');
  const [amendDate, setAmendDate] = useState(row?.written_date ?? kst());
  const set = (k: keyof typeof f) => (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>) =>
    setF((x) => ({ ...x, [k]: k === 'corpId' ? Number(e.target.value) : e.target.value }));
  const clean = (v: string) => v.replace(/[,\s원]/g, '');

  async function run(fn: () => Promise<unknown>) {
    setBusy(true); setError(null);
    try { await fn(); onSaved(); } catch (e) { setError(errMsg(e)); } finally { setBusy(false); }
  }
  const save = () => run(() => api.saveInvoice({
    id: row ? Number(row.invoice_id) : undefined, ...f, recvId: f.recvId ? Number(f.recvId) : null, driveMon: f.driveMon || null,
    supply: clean(f.supply), vat: clean(f.vat) || null,
  }));
  const field = (label: string, k: keyof typeof f, props: React.InputHTMLAttributes<HTMLInputElement> = {}) => (
    <label className="space-y-1">{label}<input className="field w-full" value={String(f[k])} onChange={set(k)} {...props} /></label>
  );

  return (
    <div className="card max-h-[90vh] space-y-4 overflow-y-auto p-5">
      {/* #404 — 페이앱 창처럼: 「계산서」 글자는 번호와 다른 칸(번호만 골라 복사되게) · 기사 신청분은 회색 글로 접수일. */}
      <div className="flex flex-wrap items-center gap-2">
        {row ? <><span className="text-sm text-ink-soft">계산서</span><h2 className="font-mono font-semibold">{row.invoice_no}</h2></>
          : <h2 className="font-semibold">수기 발행(버스인 접수 없는 건)</h2>}
        {row && <Status r={row} />}
        {row?.source === 'driver' && <span className="text-sm text-ink-soft">{row.corp_name} · {dash(row.requester)} · 접수 {dash(row.received_date)}</span>}
      </div>
      {row && <InvoiceTrail id={Number(row.invoice_id)} open={!!row.amend_of} />}
      <fieldset className="grid grid-cols-3 gap-3 text-sm">
        {!row ? (
          <label className="space-y-1">법인
            <select className="field w-full" value={f.corpId} onChange={set('corpId')}>
              {corps.filter((c) => c.invoice_code).map((c) => <option key={c.corp_id} value={c.corp_id}>{c.short_name}({c.invoice_code})</option>)}
            </select>
          </label>
        ) : <div className="space-y-1 text-ink-soft">법인<div className="py-2 text-ink">{row.corp_name}</div></div>}
        {!row ? field('계열(비움 = 연도)', 'series') : <div />}
        {field(row?.amend_of ? '작성일자(수정 발행한 날)' : '작성일자', 'writtenDate', { type: 'date' })}
        {field('거래처명', 'custName')}{field('사업자번호', 'bizNumber')}{field('이메일', 'email')}
        {field('공급가액', 'supply', { className: 'field w-full text-right' })}
        {field('세액(비우면 10%)', 'vat', { className: 'field w-full text-right' })}
        <label className="space-y-1">합계<input className="field w-full bg-canvas text-right" readOnly tabIndex={-1} value={(() => {
          // 세액을 비우면 저장 때 공급가 10%(원 미만 0 쪽 절사 — invoice.ts saveInvoice)가 들어간다 — 합계도 같은 식으로.
          const s = Number(clean(f.supply)) || 0;
          const v = clean(f.vat) === '' ? Math.trunc(s / 10) : Number(clean(f.vat)) || 0;
          return (s + v).toLocaleString();
        })()} /></label>
        {field('품목(비면 차량운행료)', 'item', { placeholder: '차량운행료' })}
        {field('정산월', 'driveMon', { type: 'month' })}{field('접수번호', 'recvId')}{field('버스인접수담당자', 'requester')}
        {field('비고(계산서에 나감)', 'itemNote', { placeholder: '예: PO Number - 5600145774' })}
        {field('메일 참조자 2 이름', 'ccName')}{field('메일 참조자 2 이메일', 'ccEmail', { type: 'email' })}
        {/* #134 — 비고1 기본은 법인 입금 계좌(지점은 상위 법인). #223 — 거래처가 원하는 계좌가 있으면 직접 적는다(비우면 법인 계좌). */}
        <label className="col-span-3 space-y-1">비고1(입금 계좌 — 비우면 법인 계좌가 들어갑니다)
          <input className="field w-full" value={f.remark1} maxLength={200} onChange={set('remark1')}
                 placeholder={(() => {
                   if (row) return row.corp_remark ?? '';
                   const c = corps.find((x) => x.corp_id === f.corpId);
                   return c?.invoice_remark ?? corps.find((x) => x.corp_id === c?.parent_corp_id)?.invoice_remark ?? '';
                 })()} /></label>
        <label className="col-span-3 space-y-1">메모(내부용 — 계산서에 안 나감)<input className="field w-full" value={f.memo} onChange={set('memo')} /></label>
        {/* #127 — 기사가 올린 사업자등록증. 기사 앱 신청분 = 비공개 보관함(토큰 필요) · 시트 신청분 = 구글드라이브 링크. */}
        {(row?.attach_name || row?.attach_url) && (
          <div className="col-span-3 text-sm">
            첨부{' '}
            {row.attach_name ? (
              <button type="button" className="text-brand-700 underline" onClick={() => {
                void fetchDriverInvoiceFileUrl(Number(row.invoice_id))
                  .then((url) => { window.open(url, '_blank'); setTimeout(() => URL.revokeObjectURL(url), 60_000); })
                  .catch((e) => setError(errMsg(e)));
              }}>📎 {row.attach_name}</button>
            ) : (
              <a className="text-brand-700 underline" href={row.attach_url!} target="_blank" rel="noreferrer">📎 기사가 올린 사업자등록증 보기</a>
            )}
          </div>
        )}
        {field('발행자 → 청구완료', 'issuer')}{field('발행일자', 'issueDate', { type: 'date' })}{field('입금일자 → 입금완료', 'paidDate', { type: 'date' })}
      </fieldset>
      {row?.source === 'driver' && !row.amend_of && <DriverNoticePanel target="invoice" id={Number(row.invoice_id)} onSent={onNotified} />}
      {row && row.source !== 'driver' && (row.request_id
        ? <RequestFilesPanel requestId={Number(row.request_id)} />
        : <p className="text-xs text-ink-faint">계산서 이미지 보내기는 발행 요청과 이어진 계산서에서만 됩니다(발행 요청 창 「기존 계산서 연결」).</p>)}
      {/* #306 — 발행 요청으로 올라온 발행 전 계산서를 영업에게 되돌린다(발행대장에서 내리고 요청은 「보완 요청」). */}
      {confirm === 'reject' && row?.request_id && (
        <div className="space-y-2 rounded-lg border border-amber-300 bg-amber-50 p-3 text-sm">
          <div>발행요청 #{row.request_id} 을(를) 반려합니다. 버스인접수담당자에게 무엇을 보충해야 하는지 적어 주세요 — 이 계산서는 발행대장에서 내려가고, 고쳐서 다시 올라오면 「발행 요청」에 다시 뜹니다.</div>
          <textarea className="field h-20 w-full" value={rejectWhy} onChange={(e) => setRejectWhy(e.target.value)} placeholder="예: 사업자번호가 거래처와 다릅니다. 사업자등록증을 첨부해 주세요." />
          <div className="flex gap-2">
            <button className="btn-primary" disabled={busy || !rejectWhy.trim()}
                    onClick={() => run(() => api.rejectInvoiceToRequest({ invoiceId: Number(row.invoice_id), reason: rejectWhy.trim() }))}>반려 보내기</button>
            <button className="btn-ghost" onClick={() => setConfirm(null)}>그만두기</button>
          </div>
        </div>
      )}
      {error && <ErrorBox message={error} />}
      <div className="flex flex-wrap gap-2">
        <button className="btn-primary" onClick={save} disabled={busy}>{busy ? '저장 중…' : row ? '저장' : '번호 매겨 저장'}</button>
        <button className="btn-ghost" onClick={onClose}>닫기</button>
        {row?.source === 'driver' && !row.amend_of && onMakeFixed && (
          <button className="btn" onClick={() => onMakeFixed(Number(row.invoice_id))} title="#206 — 이 계산서를 달마다 같은 내용으로 만듭니다(고정발행 → 이번 달 만들기)">
            매월 고정으로{row.fixed_id ? ` (지금 #${row.fixed_id})` : ''}</button>
        )}
        {/* #521(이가빈) 기사 계산서 취소 = 기사 앱과 같은 「취소신청」(−) 줄 — 발행 전·후 모두 · 손으로 마이너스 줄을 넣던 것. */}
        {row?.source === 'driver' && !row.amend_of && (confirm === 'cancel'
          ? <span className="flex flex-wrap items-center gap-2 text-sm">
              <input className="field w-48" value={cancelWhy} maxLength={200} onChange={(ev) => setCancelWhy(ev.target.value)} placeholder="취소 사유(선택 · 예: 이중신청)" />
              <button className="btn-ghost text-red-600" disabled={busy}
                      onClick={() => run(() => api.acctCancelDriverInvoice({ id: Number(row.invoice_id), reason: cancelWhy.trim() || undefined }))}>정말 취소신청</button>
              <button className="btn-ghost" onClick={() => setConfirm(null)}>그만두기</button>
            </span>
          : <button className="btn-ghost px-2 py-1 text-xs text-red-600" onClick={() => setConfirm('cancel')}
                    title="기사 앱 「취소신청」과 같은 마이너스 줄을 붙입니다(원 줄은 그대로 남습니다)">취소신청</button>)}
        {row && !row.amend_of && row.issuer && (confirm === 'amend'
          ? <span className="ml-auto flex flex-wrap items-center gap-2 text-sm">
              <label className="flex items-center gap-1" title="기재 착오 정정은 원 계산서 작성일자 그대로 · 계약 해제·금액 변동이면 그 사유가 생긴 날로 바꿔 주세요">
                수정 계산서 작성일자 <input type="date" className="field" value={amendDate} onChange={(ev) => setAmendDate(ev.target.value)} /></label>
              <button className="btn-ghost text-red-600" disabled={busy || !amendDate}
                      onClick={() => run(() => api.amendInvoice({ id: Number(row.invoice_id), writtenDate: amendDate }))}>정말 수정(마이너스) 발행</button>
            </span>
          : <button className="btn-ghost ml-auto" onClick={() => setConfirm('amend')}>수정(마이너스) 발행</button>)}
        {row && !row.issuer && row.request_id && confirm !== 'reject' && (
          <button className="btn ml-auto text-amber-800" onClick={() => setConfirm('reject')}
                  title="발행 요청을 영업에게 되돌려 보완받습니다">반려(보완 요청)</button>
        )}
        {row && !row.issuer && (confirm === 'del'
          ? <button className={`btn-ghost text-red-600 ${row.request_id ? '' : 'ml-auto'}`} onClick={() => run(() => api.deleteInvoice({ id: Number(row.invoice_id) }))}>정말 지우기</button>
          : <button className={`btn-ghost text-red-600 ${row.request_id && confirm !== 'reject' ? '' : 'ml-auto'}`} onClick={() => setConfirm('del')}>지우기(발행 전)</button>)}
      </div>
    </div>
  );
}

/**
 * #334 — 회사오더 계산서 이미지(차량 매각·거래처 요청분)를 요청한 분께 보낸다.
 * 이어진 발행 요청의 첨부(#59 `invoice_request_file`)에 붙인다 — 요청 창 대화에 「📎 파일명」 줄이 생기고
 * 요청한 분 화면에 답 알림이 뜬다(인쇄해 자리로 가져다주거나 댓글로 따로 보내던 것).
 */
/**
 * 계산서 「처리 기록」 — 누가 언제 요청·발행·취소/수정 요청·승인했는지 시간순(서버 `getInvoiceTrail`).
 * 10-27 way: 수정(−) 줄은 작성일자(= 수정 발행한 날)만 보여 요청일과 섞였다 → 수정 줄은 펼친 채로 연다.
 */
function InvoiceTrail({ id, open }: { id: number; open: boolean }) {
  const [rows, setRows] = useState<{ at: string; kind: string; who: string | null; text: string; invoice_id: string | null }[] | null>(null);
  const [err, setErr] = useState<string | null>(null);
  useEffect(() => {
    let alive = true;
    api.getInvoiceTrail({ id }).then((r) => { if (alive) setRows(r.rows); }).catch((e) => { if (alive) setErr(errMsg(e)); });
    return () => { alive = false; };
  }, [id]);
  return (
    <details open={open} className="rounded-lg border border-line bg-surface px-3 py-2 text-sm">
      <summary className="cursor-pointer font-medium">처리 기록{rows ? ` (${rows.length})` : ''}</summary>
      {err ? <p className="mt-1 text-xs text-red-600">{err}</p>
        : !rows ? <p className="mt-1 text-xs text-ink-soft">불러오는 중…</p>
        : !rows.length ? <p className="mt-1 text-xs text-ink-soft">기록이 없습니다.</p>
        : (
          <ol className="mt-1 space-y-0.5">
            {rows.map((e, i) => (
              <li key={i} className={`flex flex-wrap gap-x-2 ${Number(e.invoice_id) === id ? 'font-semibold' : ''}`}>
                <span className="w-36 shrink-0 font-mono text-xs text-ink-soft">{e.at.slice(0, 16)}</span>
                <span className={`w-28 shrink-0 ${/수정\(−\)|요청$/.test(e.kind) ? 'text-red-700' : ''}`}>{e.kind}</span>
                <span className="w-16 shrink-0">{dash(e.who)}</span>
                <span className="text-ink-soft">{e.text}</span>
              </li>
            ))}
          </ol>
        )}
    </details>
  );
}

function RequestFilesPanel({ requestId }: { requestId: number }) {
  const [files, setFiles] = useState<InvReqFile[] | null>(null);
  const [busy, setBusy] = useState(false);
  const [note, setNote] = useState<string | null>(null);
  const [err, setErr] = useState<string | null>(null);
  const [nonce, setNonce] = useState(0);
  useEffect(() => {
    let alive = true;
    api.getInvoiceRequest({ id: requestId })
      .then((r) => { if (alive) setFiles(r.files); })
      .catch((e) => { if (alive) setErr(errMsg(e)); });
    return () => { alive = false; };
  }, [requestId, nonce]);

  async function upload(list: FileList | null) {
    if (!list || list.length === 0) return;
    setBusy(true); setErr(null); setNote(null);
    const failed: string[] = [];
    for (const f of Array.from(list)) {
      try { await uploadInvoiceRequestFile(requestId, f); } catch (e) { failed.push(`${f.name} — ${errMsg(e)}`); }
    }
    setBusy(false);
    if (failed.length) setErr(`못 올린 파일: ${failed.join(' · ')}`);
    if (failed.length < list.length) setNote(`발행요청 #${requestId} 에 붙였습니다 — 요청하신 분이 요청 창에서 바로 봅니다.`);
    setNonce((n) => n + 1);
  }

  async function open(f: InvReqFile) {
    try {
      const url = await fetchInvoiceRequestFileUrl(f.id);
      window.open(url, '_blank', 'noopener');
      setTimeout(() => URL.revokeObjectURL(url), 60_000);
    } catch (e) { setErr(errMsg(e)); }
  }

  // #334 후속 — 고쳐서 다시 올릴 때 옛 파일을 내린다(요청 창 대화의 그 줄은 「(내림)」으로 남는다).
  const [delId, setDelId] = useState<number | null>(null);
  async function remove(f: InvReqFile) {
    setBusy(true); setErr(null); setNote(null);
    try {
      await api.deleteInvoiceRequestFile({ fileId: f.id });
      setNote(`${f.file_name} 을(를) 지웠습니다.`);
    } catch (e) { setErr(errMsg(e)); }
    setBusy(false); setDelId(null);
    setNonce((n) => n + 1);
  }

  return (
    <div className="space-y-2 rounded-lg border border-line bg-canvas p-3 text-sm">
      <div className="flex flex-wrap items-center gap-2">
        <span className="font-medium">계산서 이미지 보내기</span>
        <span className="text-xs text-ink-faint">발행요청 #{requestId} 에 붙어 요청하신 분이 봅니다</span>
        <label className={`btn ml-auto py-1 text-xs ${busy ? 'pointer-events-none opacity-50' : ''}`}>
          {busy ? '올리는 중…' : '📎 파일 올리기'}
          <input type="file" multiple className="hidden" accept="image/*,application/pdf"
                 onChange={(e) => { void upload(e.target.files); e.target.value = ''; }} />
        </label>
      </div>
      {files && files.length > 0 && (
        <div className="flex flex-wrap gap-1">
          {files.map((f) => (
            <span key={f.id} className="inline-flex max-w-[20rem] items-center rounded border border-line bg-white text-xs">
              <button type="button" className="truncate px-2 py-0.5 hover:underline"
                      onClick={() => void open(f)} title={`${f.created_by ?? ''} · ${f.created_at}`}>
                {f.content_type.startsWith('image/') ? '🖼' : f.content_type === 'application/pdf' ? '📕' : '📄'} {f.file_name}
              </button>
              {delId === f.id
                ? <button type="button" className="shrink-0 border-l border-line px-2 py-0.5 font-medium text-red-600" disabled={busy}
                          onClick={() => void remove(f)}>지우기</button>
                : <button type="button" className="shrink-0 border-l border-line px-1.5 py-0.5 text-ink-faint hover:text-red-600" disabled={busy}
                          onClick={() => setDelId(f.id)} title="이 파일 지우기" aria-label={`${f.file_name} 지우기`}>×</button>}
            </span>
          ))}
        </div>
      )}
      {note && <p className="text-xs text-emerald-700">{note}</p>}
      {err && <p className="text-xs text-red-600">{err}</p>}
    </div>
  );
}
